import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { HrButtons, HrFlexContainer, HrlighterGrey, HrTitleH4, Separate } from '@components/styles/hr-styles';
import { Input } from '@components/form/input';
import { Textarea } from '@components/form/textarea';
import { Modal } from '@/components/v1/Modal';
import { useTranslationWithPrefix } from '@/i18n/hooks';
import {
  CUSTOM_FIELD_LABEL_MAX_LENGTH,
  CUSTOM_FIELD_NAME_MAX_LENGTH,
  CUSTOM_FIELD_TYPES,
  MIN_CLOSED_FIELD_OPTIONS
} from '@/vacancy_request/components/template-request-form/custom-fields/constants';
import { FieldTypeSelect } from '@/vacancy_request/components/template-request-form/custom-fields/field-type-select';
import { FieldOptionsEditor } from '@/vacancy_request/components/template-request-form/custom-fields/field-options-editor';

const QuestionCard = styled.div`
  width: 100%;
  border: 1px solid ${HrlighterGrey};
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/* La pregunta ocupa más que el tipo, como en el diseño. */
const QuestionGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const buildDefaultValues = (field) => ({
  name: field?.name ?? '',
  label: field?.label ?? '',
  type: field?.type ?? CUSTOM_FIELD_TYPES.OPEN,
  options: Array.isArray(field?.options) ? field.options.map((value) => ({ value })) : []
});

const findDuplicatedIndex = (options) =>
  options.findIndex((option, index) => options.indexOf(option) !== index);

/**
 * Modal de alta/edición de un campo custom.
 *
 * Tiene su propio `useForm` a propósito: NO usa el `useFormContext` de la plantilla. Es lo que
 * sostiene el invariante "un campo confirmado siempre está completo" — mientras se edita, el
 * borrador incompleto vive acá adentro y no en el árbol del formulario principal, así que no
 * puede viajar en un submit.
 *
 * Emite sólo lo que define: `{ name, label, type, options }`. La identidad (`_id`), la pestaña
 * (`tab`), el orden y el nivel obligatorio/opcional/deshabilitado los administra el listado.
 */
export const CustomFieldModal = ({ isOpen, field, onClose, onConfirm }) => {
  const { t } = useTranslationWithPrefix('requestVacancies.requestTemplate.form.customFields');

  const {
    control,
    register,
    handleSubmit,
    formState,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    reset,
    trigger
  } = useForm({
    mode: 'all',
    defaultValues: buildDefaultValues(field)
  });

  const selectedType = watch('type');
  const isClosedType = selectedType === CUSTOM_FIELD_TYPES.CLOSED;

  // Sólo al abrir: si dependiera de la identidad de `field`, un objeto inline en el padre
  // dispararía un reset en cada render.
  useEffect(() => {
    if (!isOpen) return;

    reset(buildDefaultValues(field));
    if (field) trigger();
  }, [isOpen]);

  // El tipo y las opciones se leen con `getValues` y no con el `selectedType` del render:
  // este efecto corre en el mismo ciclo que el reset de arriba, y ahí `selectedType` todavía
  // es el valor viejo mientras el store ya tiene el nuevo. Mezclarlos borraría las opciones
  // de un campo cerrado apenas se abre para editarlo.
  useEffect(() => {
    if (!isOpen) return;

    const isClosed = getValues('type') === CUSTOM_FIELD_TYPES.CLOSED;
    const currentOptions = getValues('options') || [];

    // Se completa hasta el mínimo en vez de avisar con un error: el editor sólo dibuja errores por
    // fila, así que uno a nivel del array no se vería. Apareciendo las filas vacías, el `required`
    // que cada una ya tiene deja el Confirmar deshabilitado hasta llenarlas.
    //
    // Cubre las dos entradas: un campo nuevo, que arranca sin opciones, y uno cerrado guardado
    // antes de que el mínimo fuera dos.
    if (isClosed && currentOptions.length < MIN_CLOSED_FIELD_OPTIONS) {
      const missing = MIN_CLOSED_FIELD_OPTIONS - currentOptions.length;
      setValue('options', [...currentOptions, ...Array.from({ length: missing }, () => ({ value: '' }))]);
      return;
    }

    if (!isClosed && currentOptions.length > 0) {
      setValue('options', []);
      clearErrors('options');
    }
  }, [isOpen, selectedType, getValues, setValue, clearErrors]);

  const handleClose = () => {
    reset(buildDefaultValues(null));
    onClose();
  };

  const handleConfirm = handleSubmit((values) => {
    const options = isClosedType ? values.options.map((option) => option.value.trim()) : [];

    if (isClosedType) {
      const duplicatedIndex = findDuplicatedIndex(options);
      if (duplicatedIndex !== -1) {
        setError(`options.${duplicatedIndex}.value`, {
          type: 'duplicated',
          message: t('modal.options.duplicated')
        });
        return;
      }
    }

    onConfirm({
      name: values.name.trim(),
      label: values.label.trim(),
      type: values.type,
      options
    });

    handleClose();
  });

  return (
    // `closeOnOverlayClick={false}`: un click al costado no descarta el campo que se venía
    // cargando. Se cierra con la X, con Esc o confirmando.
    <Modal isOpen={isOpen} toggleModal={handleClose} topOffset={150} closeOnOverlayClick={false}>
      <HrFlexContainer flexDirection='column' gap='24px' alignItems='flex-start'>
        <HrFlexContainer flexDirection='column' gap='16px' alignItems='stretch'>
          <HrTitleH4>{`+ ${t('modal.title')}`}</HrTitleH4>
          <Separate margin='0' />
        </HrFlexContainer>

        <Input
          title={t('modal.name.label')}
          placeholder={t('modal.name.placeholder')}
          name='name'
          type='text'
          register={register}
          formState={formState}
          errorPosition='bottom'
          validate={{
            required: true,
            whiteSpace: true,
            maxLength: CUSTOM_FIELD_NAME_MAX_LENGTH
          }}
        />

        <QuestionCard>
          <QuestionGrid>
            <Textarea
              title={t('modal.question.label')}
              placeholder={t('modal.question.placeholder')}
              id='label'
              control={control}
              formState={formState}
              errorPosition='bottom'
              minHeight='120px'
              max={CUSTOM_FIELD_LABEL_MAX_LENGTH}
              validate={{ required: true }}
            />
            <FieldTypeSelect control={control} formState={formState} />
          </QuestionGrid>

          {isClosedType && (
            <FieldOptionsEditor control={control} register={register} formState={formState} />
          )}
        </QuestionCard>

        <HrFlexContainer justifyContent='flex-end' flexWrap='nowrap' gap='16px' padding='8px 0 0 0'>
          <HrButtons type='button' category='cancel' size='medium' onClick={handleClose}>
            {t('modal.cancel')}
          </HrButtons>
          <HrButtons
            type='button'
            category='primary'
            size='medium'
            onClick={handleConfirm}
            disabled={!formState.isValid}
          >
            {t('modal.confirm')}
          </HrButtons>
        </HrFlexContainer>
      </HrFlexContainer>
    </Modal>
  );
};

CustomFieldModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  field: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.oneOf([CUSTOM_FIELD_TYPES.OPEN, CUSTOM_FIELD_TYPES.CLOSED]),
    options: PropTypes.arrayOf(PropTypes.string)
  }),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
};

CustomFieldModal.defaultProps = {
  field: null
};
